import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { LocaleService } from "src/app/services/locale.service";
import { SanitizerService } from "src/app/shared/util/sanitizer.service";

@Injectable({
  providedIn: "root",
})
export class CanonicalUrlService {
  readonly LINK_ELEMENT_ID = "page-canonical";
  readonly URL_ORIGIN = "https://bitoasis.net/";

  constructor(
    @Inject(DOCUMENT) private doc: Document,
    private localeService: LocaleService,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizerService: SanitizerService
  ) {}

  init() {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((_: NavigationEnd) => {
        this.setCanonicalURLFromRoute();
      });

    this.setCanonicalURLFromRoute();
  }

  setCanonicalURLFromRoute() {
    const canonicalSettings = this.route.snapshot.data?.canonical;
    this.setCanonicalURL(
      canonicalSettings?.url,
      canonicalSettings?.queryParams
    );
  }

  setCanonicalURL(url: string, includeQueryParams = false) {
    const fullUrl = this.getFullUrl(url, includeQueryParams);
    const safeUrl = this.sanitizerService.sanitizeUrl(fullUrl);
    const linkElement = this.getLinkElement();

    if (linkElement) {
      this.updateCanonicalURL(safeUrl);
    } else {
      this.createCanonicalURL(safeUrl);
    }
  }

  getFullUrl(url: string, includeQueryParams = false) {
    const path = url || this.router.url;
    const locale = this.localeService.currentLocale;
    const fullUrl = new URL(`${this.URL_ORIGIN}${locale}${path}`);
    return includeQueryParams
      ? fullUrl.href
      : `${fullUrl.origin}${fullUrl.pathname}`;
  }

  getLinkElement() {
    return this.doc.getElementById(this.LINK_ELEMENT_ID);
  }

  private updateCanonicalURL(url: string) {

    const link = this.getLinkElement();

    link.setAttribute("href", url);
  }

  private createCanonicalURL(url: string) {
    const link: HTMLLinkElement = this.doc.createElement("link");
    link.id = this.LINK_ELEMENT_ID;
    link.setAttribute("rel", "canonical");
    link.setAttribute("href", url);
    this.doc.head.appendChild(link);
  }
}
